import React, { useContext, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Heading,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AppContext,
  CreatableDropdown,
  getParsedCrudData,
  Loader,
  preFormatForm,
  translateText,
  useIsAuthorisedHook,
  validateForm,
  TEXT_LENGTH,
  useActivityTracker,
  useProduct,
  COMMON_CONSTANTS,
  ConfirmationModal,
  getTranslatedText,
  useCurrencySymbols,
  CustomConfirmationModal
} from '@nile/components';
import {
  CreateServiceCatalogItem,
  ServiceCatalogItem,
  UpdateServiceCatalogItem
} from '../types';
import { includes, isEqual, omit } from 'lodash';
import {
  AccountClient,
  CREATE_SERVICE_CATEGORY,
  DELETE_SERVICE_CATEGORY,
  GET_SERVICE_CATALOG_ITEM,
  GET_SERVICE_CATEGORIES,
  UPDATE_SERVICE_CATEGORY
} from '@nile/graphql';
import TaxConfiguration from '../TaxConfiguration';
import ServiceCatalogItemCostAndPrice from './ServiceCatalogItemCostAndPrice';
import { getServiceCatalogConstraints } from '../ServiceCatalogForm/Validators';
import {
  UserGuide,
  useUserGuideContext
} from '@nile/client_management/src/Contracts/Components/UserGuidePopup';
import { USER_GUIDE } from '@nile/client_management/src/Contracts/Constants';

type ServiceCatalogOptionsType = 'PRODUCT' | 'SERVICE' | 'PACKAGE';

interface ServiceCategoryOption {
  // properties from api
  name: string;
  categoryId: string;
  // properties added for dropdown options
  id?: string;
  value?: string;
  label?: string;
  isNew?: boolean;
  editState?: boolean;
}

const serviceCatalogItemDefaultData: ServiceCatalogItem = {
  name: null,
  category: null,
  taxable: false,
  salesTax: null,
  costPrice: null,
  status: 'ACTIVE',
  description: null,
  sellingPrice: null,
  itemType: 'SERVICE',
  serviceTypeItem: null,
  offerAsWorklogItem: false
};

type CustomServiceCatalogOptionsType =
  | ServiceCatalogOptionsType
  | 'SERVICE_QUANTITY'
  | 'PACKAGE_BUNDLE';

const CATALOGUE_OPTIONS_MAP: {
  [K in CustomServiceCatalogOptionsType]: ServiceCatalogItem;
} = {
  SERVICE: {
    ...serviceCatalogItemDefaultData,
    serviceTypeItem: {
      offeringType: 'HOURS',
      hourlyServiceTypeItem: {
        ...serviceCatalogItemDefaultData?.serviceTypeItem
          ?.hourlyServiceTypeItem,
        roundUpValue: 1
      }
    }
  },
  PACKAGE: {
    ...serviceCatalogItemDefaultData,
    serviceTypeItem: {
      offeringType: 'BLOCK_HOUR',
      bundledItems: [],
      redeemableServiceBundle: {
        applicableHours: 'ALL_HOURS',
        applicableType: 'INCLUDED_ITEMS'
      }
    }
  },
  PRODUCT: {
    ...serviceCatalogItemDefaultData,
    itemType: 'PRODUCT'
  },
  SERVICE_QUANTITY: {
    ...serviceCatalogItemDefaultData,
    serviceTypeItem: {
      offeringType: 'QUANTITY'
    }
  },
  PACKAGE_BUNDLE: {
    ...serviceCatalogItemDefaultData,
    serviceTypeItem: {
      offeringType: 'SERVICE_BUNDLE',
      bundledItems: []
    }
  }
};

export const ServiceCatalogFormContext = React.createContext({
  errors: null,
  currencyCode: null,
  currencyValue: null,
  resetErrors: () => {},
  validate: (item) => {}
});

const CurrentCatalogTypeMap = {
  SERVICE_QUANTITY: 'SERVICE',
  PACKAGE_BUNDLE: 'PACKAGE'
};

const ServiceCatalogForm = ({
  mode,
  closeForm,
  clearForm,
  hideAddNew,
  updateProperties,
  disabledSections,
  createServiceItem,
  updateServiceItem,
  selectedCatologType,
  isIntegrationAddOnItem,
  serviceCatalogItemId,
  selectedCatalogItem: selectedCatalogItemFromProps
}: {
  mode: 'NEW' | 'EDIT' | 'CLONE';
  closeForm: () => void;
  clearForm?: () => void;
  hideAddNew?: boolean;
  disabledSections?: string[];
  serviceCatalogItemId?: string;
  isIntegrationAddOnItem?: boolean;
  updateProperties?: (arg0: JSON) => void;
  createServiceItem: (arg0: JSON) => Promise<unknown>;
  updateServiceItem: (arg0: JSON) => Promise<unknown>;
  selectedCatalogItem?: ServiceCatalogItem;
  selectedCatologType?: ServiceCatalogOptionsType;
}): React.ReactElement => {
  const intl = useIntl();
  const { companyInfo } = useContext<any>(AppContext);
  const isMspProduct = useProduct(COMMON_CONSTANTS.MSP);
  const currentCatalogType: ServiceCatalogOptionsType =
    CurrentCatalogTypeMap[selectedCatologType] || selectedCatologType;

  const currencyCode = companyInfo?.currency || 'USD';
  const countryCode = companyInfo?.countryCode || 'US';

  const { currencySymbolWithCode } = useCurrencySymbols();
  const currencyValue = currencySymbolWithCode;

  const categoryPermission = {
    add: useIsAuthorisedHook('serviceCatalog.add'),
    edit: useIsAuthorisedHook('serviceCatalog.edit'),
    delete: useIsAuthorisedHook('serviceCatalog.delete')
  };

  const catalogItemFromApi = useRef<ServiceCatalogItem | null>(null);

  const [
    selectedCatalogItem,
    setSelectedCatalogItem
  ] = useState<ServiceCatalogItem | null>(
    selectedCatalogItemFromProps ||
      CATALOGUE_OPTIONS_MAP[selectedCatologType || 'SERVICE']
  );

  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [saveAndAddNewLoading, setSaveAndAddNewLoading] = useState<boolean>(
    false
  );

  const isLateFee =
    selectedCatalogItem?.serviceTypeItem?.defaultUsageType === 'LATE_FEE';

  const [errors, setErrors] = useState<Record<string, string[]> | null>(null);

  const disableServiceItemTypeSwitch =
    mode === 'EDIT' ||
    selectedCatologType === 'SERVICE_QUANTITY' ||
    selectedCatologType === 'PACKAGE_BUNDLE';

  useEffect(() => {
    if (
      selectedCatologType &&
      selectedCatologType !== selectedCatalogItem?.itemType
    ) {
      setSelectedCatalogItem(
        () => CATALOGUE_OPTIONS_MAP[selectedCatologType || 'SERVICE']
      );
    }
  }, [selectedCatologType]);

  useEffect(() => {
    if (
      selectedCatalogItemFromProps &&
      !isEqual(selectedCatalogItemFromProps, selectedCatalogItem)
    ) {
      setSelectedCatalogItem(() => selectedCatalogItemFromProps);
    }
  }, [selectedCatalogItemFromProps]);

  const [serviceCategoryOptions, setServiceCategoryOptions] = useState<
    ServiceCategoryOption[]
  >([]);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const {
    loading: getServiceItemLoading,
    error: getServiceItemError
  } = useQuery(GET_SERVICE_CATALOG_ITEM, {
    client: AccountClient,
    skip: mode === 'NEW' || !serviceCatalogItemId,
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        itemId: serviceCatalogItemId
      }
    },
    onCompleted: (res) => {
      if (res?.getServiceCatalogItem) {
        let ServiceCatalogItem: ServiceCatalogItem = res?.getServiceCatalogItem;

        // saving a reference to serviceItem received from api to compare during update
        catalogItemFromApi.current = res?.getServiceCatalogItem;

        if (mode === 'CLONE') {
          ServiceCatalogItem = {
            ...omit(ServiceCatalogItem, ['itemId', 'name']),
            name: `Clone of ${ServiceCatalogItem?.name}`,
            serviceTypeItem: ServiceCatalogItem?.serviceTypeItem
              ? {
                  ...omit(ServiceCatalogItem?.serviceTypeItem, 'itemId'),
                  hourlyServiceTypeItem: ServiceCatalogItem?.serviceTypeItem
                    ?.hourlyServiceTypeItem
                    ? omit(
                        ServiceCatalogItem?.serviceTypeItem
                          ?.hourlyServiceTypeItem,
                        'itemId'
                      )
                    : null,
                  redeemableServiceBundle: ServiceCatalogItem?.serviceTypeItem
                    ?.redeemableServiceBundle
                    ? omit(
                        ServiceCatalogItem?.serviceTypeItem
                          ?.redeemableServiceBundle,
                        'itemId'
                      )
                    : null
                }
              : null
          };
        }
        setSelectedCatalogItem(ServiceCatalogItem);
      }
    }
  });

  function updateSelectedCatalogItem(key, value): void {
    setSelectedCatalogItem((selectedCatalogItem) => ({
      ...selectedCatalogItem,
      [key]: value
    }));
  }

  function updateSelectedCatalogItems(catalogItems): void {
    setSelectedCatalogItem((selectedCatalogItem) => ({
      ...selectedCatalogItem,
      ...catalogItems
    }));
  }

  function newServiceItem({ saveFormInput: formInput, saveAndCreateNew }) {
    if (selectedCatalogItem?.serviceTypeItem?.bundledItems?.length) {
      formInput = {
        ...formInput,
        serviceTypeItem: {
          ...formInput.serviceTypeItem,
          addBundledItems: selectedCatalogItem.serviceTypeItem.bundledItems?.map(
            (item) => ({
              itemId: item?.itemId
            })
          )
        }
      };
    }

    createServiceItem({
      formData: omit(formInput, ['integrationState']),
      createNew: saveAndCreateNew
    }).catch((err) => {
      useActivityTracker({
        event: `Created a ${selectedCatologType.toLocaleLowerCase()}`,
        data: {
          source:
            selectedCatologType === 'PRODUCT'
              ? null
              : selectedCatalogItem?.serviceTypeItem?.offeringType
        }
      });

      if (saveAndCreateNew) setSaveAndAddNewLoading(false);
      else setSaveLoading(false);
    });
  }

  function editServiceItem({
    saveFormInput: FormInput
  }: {
    saveFormInput: UpdateServiceCatalogItem;
  }) {
    let formInput = {
      ...FormInput,
      serviceTypeItem: FormInput?.serviceTypeItem
        ? {
            ...omit(FormInput?.serviceTypeItem, ['itemId', 'offeringType']),
            redeemableServiceBundle: FormInput?.serviceTypeItem
              ?.redeemableServiceBundle
              ? {
                  ...omit(
                    FormInput?.serviceTypeItem?.redeemableServiceBundle,
                    'itemId'
                  )
                }
              : null,
            hourlyServiceTypeItem: FormInput?.serviceTypeItem
              ?.hourlyServiceTypeItem
              ? {
                  ...omit(
                    FormInput?.serviceTypeItem?.hourlyServiceTypeItem,
                    'itemId'
                  )
                }
              : null
          }
        : null
    };

    // if (formInput?.serviceTypeItem) {
    //   formInput.serviceTypeItem = {
    //     ...omit(FormInput?.serviceTypeItem, 'itemId'),
    //     redeemableServiceBundle: FormInput?.serviceTypeItem
    //       ?.redeemableServiceBundle
    //       ? {
    //           ...omit(
    //             FormInput?.serviceTypeItem?.redeemableServiceBundle,
    //             'itemId'
    //           )
    //         }
    //       : null
    //   }
    // }

    // if (formInput?.serviceTypeItem?.redeemableServiceBundle) {
    //   formInput = {
    //     ...omit(FormInput?.serviceTypeItem, 'itemId'),
    //     redeemableServiceBundle: FormInput?.serviceTypeItem
    //       ?.redeemableServiceBundle
    //       ? {
    //           ...omit(
    //             FormInput?.serviceTypeItem?.redeemableServiceBundle,
    //             'itemId'
    //           )
    //         }
    //       : null
    //   }
    // }

    if (selectedCatalogItem?.serviceTypeItem?.bundledItems) {
      const { addedItems, deletedItems } = getParsedCrudData(
        catalogItemFromApi.current?.serviceTypeItem?.bundledItems,
        selectedCatalogItem.serviceTypeItem.bundledItems,
        'itemId'
      );

      if (addedItems.length) {
        formInput = {
          ...formInput,
          serviceTypeItem: {
            ...formInput.serviceTypeItem,
            addBundledItems: addedItems.map((item) => ({
              itemId: item?.itemId
            }))
          }
        };
      }

      if (deletedItems.length) {
        formInput = {
          ...formInput,
          serviceTypeItem: {
            ...formInput.serviceTypeItem,
            deleteBundledItems: deletedItems.map((item) => ({
              itemId: item?.itemId
            }))
          }
        };
      }
    }

    updateServiceItem({
      formData: omit(formInput, [
        'itemType',
        'offeringType',
        'integrationState'
      ])
    }).catch((err) => {
      setSaveLoading(false);
    });
  }

  function validateFormData(formData: ServiceCatalogItem) {
    const ServiceCatalogConstraints = getServiceCatalogConstraints({
      serviceItem: formData,
      productType: isMspProduct ? 'MSP' : 'IT'
    });
    const validationResult = validateForm(
      preFormatForm(formData),
      ServiceCatalogConstraints
    );
    if (validationResult) setErrors(validationResult);
    else setErrors(null);

    return !validationResult;
  }

  function handleSaveForm({
    saveAndCreateNew
  }: {
    saveAndCreateNew?: boolean;
  }) {
    return () => {
      const isValid = validateFormData(selectedCatalogItem);
      if (!isValid) {
        return;
      }
      if (saveAndCreateNew) setSaveAndAddNewLoading(true);
      else setSaveLoading(true);

      let saveFormInput: CreateServiceCatalogItem | UpdateServiceCatalogItem = {
        ...omit(
          selectedCatalogItem,
          selectedCatologType === 'PRODUCT' ||
            (selectedCatologType === 'SERVICE' &&
              selectedCatalogItem?.serviceTypeItem?.offeringType === 'QUANTITY')
            ? ['status']
            : ['status', 'offerAsWorklogItem']
        )
      };

      if (saveFormInput?.serviceTypeItem) {
        saveFormInput = {
          ...saveFormInput,
          serviceTypeItem: omit(selectedCatalogItem?.serviceTypeItem, [
            'bundledItems',
            'defaultUsageType'
          ])
        };
      }

      if (mode === 'EDIT') {
        editServiceItem({ saveFormInput });
      } else {
        newServiceItem({ saveFormInput, saveAndCreateNew });
      }
    };
  }

  function handleSelectChange(item, newValue) {
    if (newValue) {
      updateSelectedCatalogItem('category', {
        name: item.value,
        categoryId: item.categoryId
      });
    } else {
      updateSelectedCatalogItem('category', {
        id: item.id,
        name: item.value
      });
    }
  }

  const { loading, data, error } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted({ getServiceCategories }) {
      if (getServiceCategories) {
        const serviceCategories = getServiceCategories.map((el) => {
          return {
            ...el,
            value: el.name,
            label: el.name,
            editState: false,
            id: el.categoryId || el.id
          };
        });
        setServiceCategoryOptions(serviceCategories);
      }
    }
  });

  const [createServiceCategory] = useMutation(CREATE_SERVICE_CATEGORY, {
    client: AccountClient,
    onCompleted({ createServiceCategory }) {
      if (createServiceCategory) {
        const optionsCopy = [...serviceCategoryOptions];
        const optionCreatedIndex = serviceCategoryOptions.findIndex(
          ({ value }) => value === createServiceCategory.name
        );
        if (optionCreatedIndex > -1) {
          optionsCopy[optionCreatedIndex].id = createServiceCategory.categoryId;
          optionsCopy[optionCreatedIndex].categoryId =
            createServiceCategory.categoryId;
          optionsCopy[optionCreatedIndex].isNew = false;
          setServiceCategoryOptions(optionsCopy);
        }
        if (selectedOptions[0]?.value === createServiceCategory.name) {
          setSelectedOptions([optionsCopy[optionCreatedIndex]]);
          handleSelectChange(optionsCopy[optionCreatedIndex], true);
        }
      }
    }
  });

  const [updateServiceCategory] = useMutation(UPDATE_SERVICE_CATEGORY, {
    client: AccountClient
  });

  const [deleteServiceCategory] = useMutation(DELETE_SERVICE_CATEGORY, {
    client: AccountClient
  });

  const isIntegrationAddOn = isIntegrationAddOnItem;

  const SERVICE_CATALOG_OPTIONS_TYPE_MAP: Record<
    ServiceCatalogOptionsType,
    {
      header: string;
      namePlaceHolder: string;
      descriptionPlaceholder?: string;
      quantitySectionHeader: string;
    }
  > = {
    SERVICE: {
      header: `admin.serviceCatalog.type.service.${
        mode === 'EDIT' ? 'edit' : 'create'
      }`,
      namePlaceHolder: 'admin.serviceCatalog.type.service.namePlaceHolder',
      quantitySectionHeader: 'common.type'
    },
    PRODUCT: {
      header: isMspProduct
        ? `admin.serviceCatalog.type.product.${
            mode === 'EDIT' ? 'edit' : 'create'
          }`
        : `admin.serviceCatalog.${mode === 'EDIT' ? 'edit' : 'new'}`,
      namePlaceHolder: isMspProduct
        ? 'admin.serviceCatalog.type.product.namePlaceHolder'
        : 'common.enter.name',
      quantitySectionHeader:
        'admin.serviceCatalog.type.product.quantitySectionHeader'
    },
    PACKAGE: {
      header: `admin.serviceCatalog.type.package.${
        mode === 'EDIT' ? 'edit' : 'create'
      }`,
      namePlaceHolder: 'admin.serviceCatalog.type.package.namePlaceHolder',
      quantitySectionHeader: 'common.type'
    }
  };

  const sericeCatalogOption =
    SERVICE_CATALOG_OPTIONS_TYPE_MAP[
      currentCatalogType || selectedCatalogItem?.itemType
    ];

  function handlePropertyChange(key: string) {
    return (value) => {
      updateSelectedCatalogItem(key, value);
    };
  }

  useEffect(() => {
    const selectedOption = serviceCategoryOptions.find((el) => {
      if (el.id) return el.id === selectedCatalogItem?.category?.categoryId;
      return el.name === selectedCatalogItem?.category?.name;
    });
    if (selectedOption) setSelectedOptions([selectedOption]);
  }, [selectedCatalogItem?.category?.categoryId, serviceCategoryOptions]);

  const {
    open: openUserGuide,
    isVisible: isUserGuideVisible
  } = useUserGuideContext();

  if (getServiceItemLoading) {
    return <Loader />;
  }

  const isIntegratedWithPax8 = !!selectedCatalogItem?.integrationState?.pax8;

  return (
    <ServiceCatalogFormContext.Provider
      value={{
        errors: errors,
        currencyCode: currencyCode,
        validate: validateFormData,
        currencyValue: currencyValue,
        resetErrors: () => setErrors(null)
      }}
    >
      <div className="fx actr divider p20 pl40 pr40 g10">
        <Heading element="h1">
          <FormattedMessage id={sericeCatalogOption?.header} />
        </Heading>
        {isMspProduct && !isUserGuideVisible && (
          <div className="ptr linkText" onClick={openUserGuide}>
            <FormattedMessage id="admin.serviceCatalog.help.whats.this" />
          </div>
        )}
        <div className="mlauto">
          <ButtonGroup>
            <Button plainSecondary onClick={closeForm}>
              <FormattedMessage id="common.cancel" />
            </Button>
            {!hideAddNew && mode !== 'EDIT' && (
              <Button
                icon={<SVGIcon name="Add" />}
                loading={saveAndAddNewLoading}
                onClick={handleSaveForm({ saveAndCreateNew: true })}
              >
                <FormattedMessage id="app.common.save.and.add.new" />
              </Button>
            )}
            {mode === 'EDIT' &&
            selectedCatalogItem?.serviceTypeItem?.offeringType ===
              'SERVICE_BUNDLE' ? (
              <CustomConfirmationModal
                title={getTranslatedText(intl, 'common.warning')}
                confirmText={getTranslatedText(intl, 'common.yes.continue')}
                description={getTranslatedText(
                  intl,
                  'admin.serviceCatalog.serviceBundle.confirmation'
                )}
                handleConfirmation={handleSaveForm({ saveAndCreateNew: false })}
                modalActivator={
                  <Button
                    primary
                    loading={saveLoading}
                    icon={<SVGIcon name="Save" />}
                  >
                    <FormattedMessage id="common.save" />
                  </Button>
                }
                zIndex={1000}
              />
            ) : (
              <Button
                primary
                loading={saveLoading}
                icon={<SVGIcon name="Save" />}
                onClick={handleSaveForm({ saveAndCreateNew: false })}
              >
                <FormattedMessage id="common.save" />
              </Button>
            )}
          </ButtonGroup>
        </div>
      </div>
      <UserGuide.Popup
        type={currentCatalogType as USER_GUIDE}
        offset={{ top: '10px' }}
      >
        <div
          className={`fx col g40 p20 pl40 pr40 scroll ${
            isMspProduct ? 'ch-80' : 'fhp'
          }`}
        >
          <div className="fx col g20">
            <TextField
              value={selectedCatalogItem?.name}
              label="Service Name"
              labelHidden
              onChange={handlePropertyChange('name')}
              placeholder={translateText(sericeCatalogOption?.namePlaceHolder)}
              autoComplete="off"
              fontSize="fontLarge"
              error={errors?.name?.[0]}
              size="medium"
              maxLength={TEXT_LENGTH.size_5}
              disabled={isIntegratedWithPax8}
            />
            <TextField
              value={selectedCatalogItem?.description}
              label="Enter Description"
              labelHidden
              onChange={handlePropertyChange('description')}
              placeholder={translateText(
                sericeCatalogOption?.descriptionPlaceholder ||
                  'admin.serviceCatalog.itemDescription.placeholder'
              )}
              autoComplete="off"
              size="medium"
              maxLength={TEXT_LENGTH.size_9}
            />
            <div className="w500 ptr">
              <TextStyle variation="subdued">
                <FormattedMessage id="attribute.category" />
              </TextStyle>
              <CreatableDropdown
                selectedOptions={selectedOptions}
                options={serviceCategoryOptions}
                updateOptionsList={(modifiedOptions) =>
                  setServiceCategoryOptions(modifiedOptions)
                }
                updateSelectedOptions={(selectedOptions) => {
                  const selectedOption = selectedOptions[0];
                  if (selectedOption) {
                    handleSelectChange(selectedOption, true);
                    setSelectedOptions([selectedOption]);
                  } else {
                    handleSelectChange({ value: null }, true);
                    setSelectedOptions([]);
                  }
                }}
                isCreate={!!categoryPermission?.add}
                isEdit={!!categoryPermission?.edit}
                isDelete={!!categoryPermission?.delete}
                onOptionCreate={(option) => {
                  createServiceCategory({
                    variables: {
                      category: { name: option.value }
                    }
                  });
                }}
                onOptionEdit={(option) => {
                  updateServiceCategory({
                    variables: {
                      categoryId: option.id,
                      category: { categoryId: option.id, name: option.value }
                    }
                  });
                }}
                onOptionDelete={(option) => {
                  deleteServiceCategory({
                    variables: {
                      categoryIds: [option.id]
                    }
                  });
                }}
                disabled={
                  isLateFee ||
                  isIntegrationAddOn ||
                  (disabledSections &&
                    includes(disabledSections, 'serviceCategory'))
                }
              />
            </div>
          </div>
          {isMspProduct && (
            <>
              <div className="grid g20">
                <Heading element="h3">
                  <FormattedMessage
                    id={sericeCatalogOption?.quantitySectionHeader}
                  />
                </Heading>
                <ServiceCatalogItemCostAndPrice
                  selectedCatologType={currentCatalogType}
                  selectedCatalogItem={selectedCatalogItem}
                  handlePropertyChange={updateSelectedCatalogItem}
                  handlePropertiesChange={updateSelectedCatalogItems}
                  disableServiceItemTypeSwitch={disableServiceItemTypeSwitch}
                  disableServiceCatalogItemCostAndPrice={!!isLateFee}
                />
              </div>
              <div className="grid g10">
                <Heading element="h3">
                  <FormattedMessage id="admin.serviceCatalog.taxConfiguration" />
                </Heading>
                <TaxConfiguration
                  // [TODO] - Need to check and change taxconfig file...
                  taxable={selectedCatalogItem?.taxable}
                  salesTax={selectedCatalogItem?.salesTax}
                  disabledSections={disabledSections}
                  handlePropChange={updateSelectedCatalogItem}
                  handlePropertiesChange={updateSelectedCatalogItems}
                />
              </div>
            </>
          )}
        </div>
      </UserGuide.Popup>
    </ServiceCatalogFormContext.Provider>
  );
};

export default ServiceCatalogForm;
